/**
 * Paragraph Extraction from OCR Data
 *
 * Groups OCR lines into logical paragraphs based on:
 * - Vertical spacing (gaps indicate paragraph breaks)
 * - RTL text flow for Arabic
 *
 * Uses existing OCR data from pdfStorage to avoid re-processing.
 */

import { OcrDocument, OcrLine, OcrBBoxNorm } from "../ocrTypes.js";
import { PdfParagraph } from "./translationTypes.js";

/**
 * Threshold for paragraph break detection.
 * If vertical gap between lines exceeds this percentage of page height,
 * it's considered a new paragraph.
 */
const PARAGRAPH_GAP_THRESHOLD = 0.025; // 2.5% of page height

/**
 * Minimum text length for a paragraph to be included.
 * Filters out single characters, page numbers, etc.
 */
const MIN_PARAGRAPH_LENGTH = 10;

/**
 * Extract paragraphs from a single page of OCR data.
 *
 * @param docId - Document ID for generating unique paragraph IDs
 * @param ocrDoc - Full OCR document containing all pages
 * @param pageIndex - 0-indexed page number to extract from
 * @returns Array of paragraphs with text and bounding boxes
 */
export function extractParagraphsFromPage(
  docId: string,
  ocrDoc: OcrDocument,
  pageIndex: number
): PdfParagraph[] {
  const page = ocrDoc.pages.find((p) => p.pageIndex === pageIndex);
  if (!page || page.lines.length === 0) {
    return [];
  }

  const paragraphs: PdfParagraph[] = [];
  let currentLines: OcrLine[] = [];
  let paragraphIndex = 0;

  for (let i = 0; i < page.lines.length; i++) {
    const line = page.lines[i];
    const prevLine = i > 0 ? page.lines[i - 1] : null;

    // Check for paragraph break
    if (prevLine && isNewParagraph(prevLine, line)) {
      // Save current paragraph if it has enough content
      if (currentLines.length > 0) {
        const para = createParagraph(docId, pageIndex, paragraphIndex, currentLines);
        if (para.text.length >= MIN_PARAGRAPH_LENGTH) {
          paragraphs.push(para);
          paragraphIndex++;
        }
        currentLines = [];
      }
    }

    currentLines.push(line);
  }

  // Don't forget the last paragraph
  if (currentLines.length > 0) {
    const para = createParagraph(docId, pageIndex, paragraphIndex, currentLines);
    if (para.text.length >= MIN_PARAGRAPH_LENGTH) {
      paragraphs.push(para);
    }
  }

  return paragraphs;
}

/**
 * Extract paragraphs from multiple pages.
 *
 * @param docId - Document ID
 * @param ocrDoc - Full OCR document
 * @param startPage - First page (0-indexed)
 * @param endPage - Last page (0-indexed, inclusive)
 * @returns Array of paragraphs from all specified pages
 */
export function extractParagraphsFromPageRange(
  docId: string,
  ocrDoc: OcrDocument,
  startPage: number,
  endPage: number
): PdfParagraph[] {
  const allParagraphs: PdfParagraph[] = [];

  for (let pageIndex = startPage; pageIndex <= endPage; pageIndex++) {
    const pageParagraphs = extractParagraphsFromPage(docId, ocrDoc, pageIndex);
    allParagraphs.push(...pageParagraphs);
  }

  return allParagraphs;
}

/**
 * Determine if current line starts a new paragraph.
 * Based on vertical gap between lines.
 */
function isNewParagraph(prevLine: OcrLine, currentLine: OcrLine): boolean {
  // Calculate vertical gap (top of current - bottom of previous)
  const verticalGap = currentLine.bbox.y0 - prevLine.bbox.y1;

  // If gap exceeds threshold, it's a new paragraph
  return verticalGap > PARAGRAPH_GAP_THRESHOLD;
}

/**
 * Create a paragraph from a group of OCR lines.
 */
function createParagraph(
  docId: string,
  pageIndex: number,
  paragraphIndex: number,
  lines: OcrLine[]
): PdfParagraph {
  // Compute bounding box encompassing all lines
  const bbox: OcrBBoxNorm = {
    x0: Math.min(...lines.map((l) => l.bbox.x0)),
    y0: Math.min(...lines.map((l) => l.bbox.y0)),
    x1: Math.max(...lines.map((l) => l.bbox.x1)),
    y1: Math.max(...lines.map((l) => l.bbox.y1)),
  };

  // Concatenate text from all lines
  // For RTL Arabic, lines are typically already in reading order
  const text = lines.map((l) => l.text).join(" ");

  return {
    id: `${docId}-p${pageIndex}-para${paragraphIndex}`,
    pageIndex,
    text: text.trim(),
    bbox,
  };
}

/**
 * Get the center Y position of a paragraph (normalized).
 * Useful for positioning translation blobs.
 */
export function getParagraphCenterY(paragraph: PdfParagraph): number {
  return (paragraph.bbox.y0 + paragraph.bbox.y1) / 2;
}

/**
 * Get the right edge X position of a paragraph (normalized).
 * Useful for positioning connection lines.
 */
export function getParagraphRightX(paragraph: PdfParagraph): number {
  return paragraph.bbox.x1;
}
